import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useContext, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { AuthContext } from "../contexts/AuthContext";
import { storeToLocalStorage } from "../utils/index";
import LoadingScreen from "./LoadingScreen";
import React, { Suspense, lazy } from "react";
import axiosInstance from "../utils/axiosInstance";

const PharmDashboard = lazy(() => import("./../pages/PharmDashboard"));

const FormContainer = styled.div`
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
  border-radius: 0 40rem 0 0;
  padding: 15rem;
  gap: 15rem;

  @media screen and (max-width: 1024px) {
    gap: 3rem;
    height: 100vh;
  }

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 10rem;

    width: 100%;
    gap: 3rem;
    height: auto; /* Allow content to dictate height */
  }

  @media screen and (max-width: 480px) {
    padding: 1rem;
    gap: 2rem;
  }

  #form {
    @media screen and (max-width: 480px) {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      margin-top: 8.5rem;
      margin-left: 1rem;
      padding-right: 1rem;
      width: 100%;
    }
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  font-size: 6rem;
  color: #38a169;
  font-weight: bold;
  margin-right: 4rem;

  @media screen and (max-width: 1024px) {
    font-size: 4rem;
    margin-right: 2rem;
  }

  @media screen and (max-width: 768px) {
    font-size: 3rem;
    margin-right: 0; /* Remove margin for small screens */
    margin-top: 2rem; /* Add space above the logo */
    height: auto; /* Allow height to adjust based on content */
  }

  @media screen and (max-width: 480px) {
    font-size: 2.5rem;
    margin-right: 0;
    margin-top: 1rem;
  }
`;

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false); // Loading state
  const { userData, setUserData } = useContext(AuthContext);

  const notify = (msg) => toast(msg);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      if (!email || !password) {
        notify("Please fill in all fields");
        return;
      }

      const res = await axiosInstance.post("auth/login/pharmacist", {
        email,
        password,
      });

      if (res.data.status === "success") {
        notify("Login Successful");
        storeToLocalStorage("userData", res.data.data);
        setUserData(res.data.data);
        setLoading(true);
        setTimeout(() => {
          setLoading(false);
          navigate("/pharmdashboard/pharmhome");
        }, 5000);
      }
    } catch (err) {
      notify("Login Failed");
      console.log(err);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="bg-green-600 h-[100vh] flex items-center justify-center">
      {/* Form Section */}
      <FormContainer>
        <div>
          <form
            id="form"
            onSubmit={handleLogin}
            className="md:w-[500px] flex flex-col items-center justify-center h-full w-full max-w-[400px] mt-[100px] md:mt-[0px]"
          >
            <label
              htmlFor="email"
              className="block mb-2 font-semibold text-left w-full"
            >
              Email:
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              className="w-full p-2 mb-4 border border-gray-300 rounded"
              onChange={(e) => setEmail(e.target.value)}
            />

            <label
              htmlFor="password"
              className="block mb-2 font-semibold text-left w-full"
            >
              Password:
            </label>

            <div className="relative w-full">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                required
                className="w-full p-2 mb-4 border border-gray-300 rounded"
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 mb-5"
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>

            <button
              type="submit"
              className="w-full bg-green-600 text-white py-2 rounded-md font-bold"
            >
              Login
            </button>

            {/* Sign-up link */}
            <div className="mt-10 flex gap-5">
              <p>Don't have an account?</p>
              <button
                onClick={() => navigate("/auth/register/pharm")}
                className="text-blue-500 hover:underline"
              >
                Sign Up
              </button>
            </div>

            {/* forgot password */}
            <div className="mt-10 flex gap-5">
              <button
                onClick={() => navigate("/auth/forgotpassword/pharm")}
                className="text-blue-500 hover:underline"
              >
                Forgot Password?
              </button>
            </div>
          </form>
        </div>

        <LogoContainer id="logo">
          <h1 className="text-green-600">υγεία</h1>
        </LogoContainer>
      </FormContainer>

      {/* Toast Notifications */}
      <ToastContainer />
    </div>
  );
};

export default Login;
